var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
var statusPickerSx = {
    display: 'inline-flex',
    flexWrap: 'wrap',
    '& .MuiToggleButtonGroup-grouped': {
        position: 'relative',
        minHeight: { xs: 36, sm: 40 },
        px: 2,
        boxSizing: 'border-box',
        borderColor: 'rgba(0, 0, 0, 0.23)',
        color: 'text.primary',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1,
        textTransform: 'none',
        transition: function (theme) { return theme.transitions.create(['background-color', 'border-color', 'color', 'padding-left', 'padding-right'], {
            duration: theme.transitions.duration.shortest,
        }); },
        '&.Mui-selected': {
            pl: 3.5,
            pr: 2,
            bgcolor: 'action.selected',
            color: 'primary.main',
        },
        '&:hover': {
            borderColor: 'text.primary',
            zIndex: 1,
        },
        '&.Mui-selected:hover': {
            bgcolor: 'action.selected',
        },
        '&.Mui-focusVisible': {
            borderColor: 'primary.main',
            borderWidth: 2,
            zIndex: 1,
        },
        '&.Mui-disabled': {
            color: 'text.disabled',
            borderColor: 'action.disabled',
            bgcolor: 'action.disabledBackground',
            cursor: 'not-allowed',
            opacity: 1,
            pointerEvents: 'auto',
            '&:hover': {
                borderColor: 'action.disabled',
                bgcolor: 'action.disabledBackground',
            },
        },
        '&.Mui-selected.Mui-disabled': {
            color: 'text.disabled',
            bgcolor: 'action.disabledBackground',
        },
    },
    '& .NewStatusPicker-check': {
        position: 'absolute',
        left: 8,
        top: '50%',
        fontSize: 16,
        pointerEvents: 'none',
        transform: 'translateY(-50%) scale(1)',
        animation: 'NewStatusPicker-checkIn 120ms ease-out',
    },
    '@keyframes NewStatusPicker-checkIn': {
        from: {
            opacity: 0,
            transform: 'translate(-4px, -50%) scale(0.75)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(-50%) scale(1)',
        },
    },
};
/**
 * Single-select swaps 16px of left padding for 28px when a chip is picked, so the check has a
 * gutter to appear in and the label slides over. Exactly one chip is selected at any time, so the
 * 12px moves from one chip to another and the group's own width never changes.
 *
 * Multi-select breaks that invariant: every chip the user ticks adds its own 12px, so the group
 * grows as it is filled and a wrapping row reflows under the cursor. Here the gutter is reserved on
 * every chip instead, on both sides so the label stays centred — the width is then the same
 * whatever is selected, and the check still fades in exactly as it does in single-select.
 */
var multiSelectSx = {
    '& .MuiToggleButtonGroup-grouped': {
        px: 3.5,
        '&.Mui-selected': {
            pl: 3.5,
            pr: 3.5,
        },
    },
    // MUI merges two neighbouring selected buttons into one block by dropping the second one's left
    // border, which costs it a pixel. Harmless when only one can be selected; in a row of ticked
    // chips it is the same creeping reflow as the padding, one pixel per neighbour. The transparent
    // border and the overlap are what every other button in the group already has. Written as one
    // flat selector because `&.x + &.x` expands to the whole parent chain on both sides.
    '& .MuiToggleButtonGroup-grouped.Mui-selected + .MuiToggleButtonGroup-grouped.Mui-selected': {
        marginLeft: '-1px',
        borderLeft: '1px solid transparent',
    },
};
var NewStatusPicker = function (props) {
    // `props` is kept whole rather than destructured: `value` and `onChange` only narrow to their
    // single- or multi-select shape while TypeScript can still see `props.multiple` beside them.
    var options = props.options, _a = props.disabled, disabled = _a === void 0 ? false : _a, ariaLabel = props.ariaLabel, sx = props.sx;
    var baseSx = props.multiple ? [statusPickerSx, multiSelectSx] : [statusPickerSx];
    var mergedSx = sx ? __spreadArray(__spreadArray([], baseSx, true), (Array.isArray(sx) ? sx : [sx]), true) : baseSx;
    var selectedValues = props.multiple ? props.value : [props.value];
    return (_jsx(ToggleButtonGroup, __assign({ exclusive: !props.multiple, color: "primary", size: "small", value: props.value, "aria-label": ariaLabel, disabled: disabled, onChange: function (_, nextValue) {
            if (props.multiple) {
                props.onChange((nextValue !== null && nextValue !== void 0 ? nextValue : []));
            }
            else if (nextValue !== null) {
                props.onChange(nextValue);
            }
        }, sx: mergedSx }, { children: options.map(function (option) {
            var selected = selectedValues.includes(option.value);
            return (_jsxs(ToggleButton, __assign({ value: option.value, disabled: option.disabled }, { children: [selected && _jsx(CheckRoundedIcon, { "aria-hidden": true, className: "NewStatusPicker-check" }), option.label] }), option.value));
        }) })));
};
export default NewStatusPicker;
